/**
 * The course wizard is made up of 3 separate views
 * CourseWizardForm: allows the user to answer a series of questions about role, goals, etc...
 * CourseWizardLoadingSpinner: Displays a "Getting recommendations..." loader
 * CourseWizardResults: Displays the results to the user
 * 
 * Each view is toggled by three state properties:
 * - showForm
 * - showLoader
 * - showResults
 * 
 * By default, showForm is true while the others are false.
 *
 * When the form is submitted, showForm becomes false while showLoader is true.
 * 
 * After a short delay, showLoader becomes false while showResults becomes true.
**/

import { fetchCourses } from '../fetchCourses.js'
import { useLocalStorage } from '../hooks/index.js'
import { LoadingSpinner } from './LoadingSpinner.js'
import { CourseWizardResultsItem } from './CourseWizardResultsItem.js'
import { SelectMenu } from './SelectMenu.js'
import { Card } from './Card.js'
import { useState, useEffect, Fragment } from '@wordpress/element'
import { useLocation } from 'react-use'
import {
  F,
  anyPass,
  equals,
  isNil,
  pipe,
  allPass,
  propSatisfies,
  has
} from 'ramda';

// Checks is a course has its `isVisible` property set to true
// and if its `status` property is st to published
const isCourseVisibleAndPublished = course => course.isVisible && course.status === 'publish';


const maybeRenderCourses = ({ error,
  courses
}) => {
  if (error) {
    return <div>Error: {error.message}</div>;
  }
  return courses.length > 0 && courses.filter(isCourseVisibleAndPublished).length <= 0
    ? (<p>We do not currently have a course that matches that criteria. Please refine your filters above for additional recommendations.</p>)
    : courses.sort((a, b) => {
      if (a.acf.order === b.acf.order) return a.title < b.title ? -1 : 1
      return Number(a.acf.order || 0) - Number(b.acf.order || 0)
    }).map(props => (<CourseWizardResultsItem {...props} key={props.id} />))
}

const fetchCourseThumbnails = async (courses = []) => {
  const courseWithThumbs = [];
  // Loop through each course
  for (const course of courses) {
    // get the salsa content id from the shortcode
    const match = course?.acf?.thumb?.match(/\[scd_(?<assetId>\d*)/)
    // fetch the asset from salsa
    const thumbnailUrl = match?.groups?.assetId ? await fetch(`https://salsa.scaledagile.com/wp-json/wp/v2/sai_content_dam/${match?.groups?.assetId || ''}`)
      .then(r => r.json())
      // get the image url
      .then(a => a.acf.editor_image)
      .catch(() => '') : ''

    // add the thumbnail prop to the course object
    courseWithThumbs.push({
      ...course,
      thumbnailUrl,
      isVisible: true
    })
  }

  return courseWithThumbs
}

const setCourseVisibility = ({ role, goal, business, experience, isFormSubmitted = false, courses = [] } = []) => {
  console.log(courses)
  return courses.map(course => ({
    ...course,
    isVisible: (!role || !goal || !business || !experience) || course.acf?.conditions?.find(condition => {

      return condition.role.toLowerCase().trim().split(' ').join('-') === role.toLowerCase().trim().split(' ').join('-')
        && condition.goal.toLowerCase().trim().split(' ').join('-') === goal.toLowerCase().trim().split(' ').join('-')
        && condition.experience.toLowerCase().trim().split(' ').join('-') === experience.toLowerCase().trim().split(' ').join('-')
        && condition.business.toLowerCase().trim().split(' ').join('-') === business.toLowerCase().trim().split(' ').join('-')
    }) || false
  }))
}

const getOptionsFromCourses = (opt, courses) => {
  const options = [...new Set(courses.flatMap(c => c.acf?.conditions?.map(condition => condition[opt]) || []))]
    .map((label = '') => ({
      label: label,
      value: label.toLowerCase().trim().split(' ').join('-'),
      key: label.toLowerCase().trim().split(' ').join('-'),
      isSelected: false,
      isDisabled: false
    })).sort((a, b) => (a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1))
  return options
}

const getOptionValue = (options = []) => options.find(opt => opt.isSelected)?.value;

const updateSelectedOption = (value) => (options) => {
  return options.map(opt => ({
    ...opt,
    isSelected: opt.value === value
  }))
}

const parseQueryParams = (location) => {
  return location.search
    .replace('?', '')
    .split('&')
    .map(i => i.split('='))
    .reduce((obj, [key, value]) => ({ ...obj, [key]: decodeURIComponent(value) }), {})
}

function titleCase(str) {
  return str.toLowerCase().split(' ').map(function (word) {
    return (word.charAt(0).toUpperCase() + word.slice(1));
  }).join(' ');
}



export function CourseWizard() {
  const [courses, setCourses] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [goalOptions, setGoalOptions] = useState([])
  const [businessOptions, setBusinessOptions] = useState([])
  const [experienceOptions, setExperienceOptions] = useState([])
  const [error, setError] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(!courses || courses.length < 1)
  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [showLoader, setShowLoader] = useState(false)
  const [isValidated, setIsValidated] = useState(false)
  const { search, href, hash } = useLocation();

  const handleFormSubmit = (event) => {
    event.preventDefault()
    const url = new URL(href)
    const urlParams = new URLSearchParams(search)
    urlParams.set('validated', true)
    urlParams.set('formSubmitted', true)
    url.search = urlParams.toString()
    if (url.href !== href) {
      history.pushState({}, document.title, url.href)
    }

  }

  useEffect(() => {
    // const url = new URL(href)
    const urlParams = new URLSearchParams(search)
    const lang = urlParams.get('lang') || 'en'
    console.log(lang)
    fetchCourses(lang)
      .then(fetchCourseThumbnails)
      .then(courses => {
        console.log('courses', courses)
        setCourses(courses)
      })
      .then(F)
      .then(setIsLoading)
  }, [search])

  useEffect(() => {
    // console.log({ search, href, hash })
    const url = new URL(href)
    const urlParams = new URLSearchParams(search)
    const showResults =
      // urlParams.get('validated')
      urlParams.get('goal')
      && urlParams.get('role')
      && urlParams.get('experience')
      && urlParams.get('business')

    url.hash = showResults ? '#results' : ''

    if (url.href !== href) {
      location.hash = ''
      location.hash = url.hash
      // history.pushState({}, document.title, url.href)
    }


    if (hash === '#results') {
      const updateScrollPosition = setInterval(() => {
        const element = document.querySelector(hash)
        if (element) {
          element.scrollIntoView()
          clearInterval(updateScrollPosition)
        }
      }, 300)
    }
  }, [hash])




  return (
    <Fragment>
      <div className="max-w-screen-xl mx-auto grid lg:grid-cols-12 gap-6 content-start py-0 pb-10">



        {/* <div className="lg:p-0 lg:col-start-3 lg:col-end-11">

          <Card>
            <h3 className="text-xl font-sans mb-6 font-bold">Get recommendations</h3>


            <form
              onSubmit={handleFormSubmit}
              className="grid gap-8 transition-all"
            >
              <SelectMenu
                id="roleSelect"
                label="What is your role?"
                options={getOptionsFromCourses('role', courses || []).map(opt => ({ ...opt, label: titleCase(opt.label) }))}
                value={(new URLSearchParams(search)).get('role') || -1}
                onChange={(e) => {
                  const url = new URL(href)
                  const urlParams = new URLSearchParams(search)
                  urlParams.delete('formSubmitted')
                  urlParams.set('role', e.target.value)
                  if (urlParams.get('role') == -1) urlParams.delete('role')
                  url.search = urlParams.toString()
                  if (url.href !== href) {
                    history.pushState({}, document.title, url.href)
                  }
                }}
                required={true}
                invalid={pipe(
                  Object.fromEntries,
                  allPass([
                    propSatisfies(anyPass([isNil, equals("-1")]), 'role'),
                    has('validated')
                  ])
                )([...(new URLSearchParams(search)).entries()])}
                errorLabel='Please select your role'
              />

              <SelectMenu
                id="goalSelect"
                label="What are your goals?"
                options={getOptionsFromCourses('goal', courses || [])}
                value={(new URLSearchParams(search)).get('goal') || -1}
                onChange={(e) => {
                  const url = new URL(href)
                  const urlParams = new URLSearchParams(search)
                  urlParams.delete('formSubmitted')
                  urlParams.set('goal', e.target.value)
                  if (urlParams.get('goal') == -1) urlParams.delete('goal')
                  url.search = urlParams.toString()
                  if (url.href !== href) {
                    history.pushState({}, document.title, url.href)
                  }
                }}
                required={true}
                invalid={pipe(
                  Object.fromEntries,
                  allPass([
                    propSatisfies(anyPass([isNil, equals("-1")]), 'goal'),
                    has('validated')
                  ])
                )([...(new URLSearchParams(search)).entries()])}
                errorLabel='Please select your goals'
              />

              <SelectMenu
                id="businessSelect"
                label="Which best describes your business?"
                options={getOptionsFromCourses('business', courses || [])}
                value={(new URLSearchParams(search)).get('business') || -1}
                onChange={(e) => {
                  const url = new URL(href)
                  const urlParams = new URLSearchParams(search)
                  urlParams.delete('formSubmitted')
                  urlParams.set('business', e.target.value)
                  if (urlParams.get('business') == -1) urlParams.delete('business')
                  url.search = urlParams.toString()
                  if (url.href !== href) {
                    history.pushState({}, document.title, url.href)
                  }
                }}
                required={true}
                invalid={pipe(
                  Object.fromEntries,
                  allPass([
                    propSatisfies(anyPass([isNil, equals("-1")]), 'business'),
                    has('validated')
                  ])
                )([...(new URLSearchParams(search)).entries()])}
                errorLabel='Please select your business type'
              />

              <SelectMenu
                id="experienceSelect"
                label="Which best describes your experience with SAFe?"
                options={getOptionsFromCourses('experience', courses || [])}
                value={(new URLSearchParams(search)).get('experience') || -1}
                onChange={(e) => {
                  const url = new URL(href)
                  const urlParams = new URLSearchParams(search)
                  urlParams.delete('formSubmitted')
                  urlParams.set('experience', e.target.value)
                  if (urlParams.get('experience') == -1) urlParams.delete('experience')
                  url.search = urlParams.toString()
                  if (url.href !== href) {
                    history.pushState({}, document.title, url.href)
                  }
                }}
                required={true}
                invalid={pipe(
                  Object.fromEntries,
                  allPass([
                    propSatisfies(anyPass([isNil, equals("-1")]), 'experience'),
                    has('validated')
                  ])
                )([...(new URLSearchParams(search)).entries()])}
                errorLabel='Please select you level of experience'
              />

              <div className="wp-block-buttons grid grid-flow-col gap-2 m-0">
                <div className="wp-block-button text-base font-sans w-full">
                  <button
                    type="submit"
                    className="wp-block-button__link w-full"
                  >See which course is right for you
                  </button>
                </div>
              </div>

            </form>
          </Card>
        </div> */}


        <div className="col-span-12">
          <a id="results"></a>
          {/* <h2 className="text-3xl font-sans m-0">Courses for you</h2> */}
          {/* <p className="text-base font-sans m-0 mb-6">Recommendations based on your selections</p> */}
          {/* <div className={`gap-6 grid grid-cols-4 grid-flow-col mb-6 mx-auto overflow-hidden ${!isFormSubmitted ? 'hidden' : ''}`}>
            <div className="font-sans overflow-hidden text-base">
              <SelectMenu
                options={roleOptions}
                value={getOptionValue(roleOptions)}
                onChange={(e) => setRoleOptions(updateSelectedOption(e.target.value))}
                placeholder="Role"
              />
            </div>
            <div className="font-sans overflow-hidden text-base">
              <SelectMenu
                options={goalOptions}
                value={getOptionValue(goalOptions)}
                onChange={(e) => setGoalOptions(updateSelectedOption(e.target.value))}
                placeholder="Goal"
              />
            </div>
            <div className="font-sans overflow-hidden text-base">
              <SelectMenu
                options={businessOptions}
                value={getOptionValue(businessOptions)}
                onChange={(e) => setBusinessOptions(updateSelectedOption(e.target.value))}
                placeholder="Business"
              />
            </div>
            <div className="font-sans overflow-hidden text-base">
              <SelectMenu
                options={experienceOptions}
                value={getOptionValue(experienceOptions)}
                onChange={(e) => setExperienceOptions(updateSelectedOption(e.target.value))}
                placeholder="Experience"
              />
            </div>
          </div> */}

          <div className="gap-6 grid">


            {maybeRenderCourses({
              error, courses: setCourseVisibility({
                goal: (new URLSearchParams(search)).get('goal'),
                role: (new URLSearchParams(search)).get('role'),
                business: (new URLSearchParams(search)).get('business'),
                experience: (new URLSearchParams(search)).get('experience'),
                isFormSubmitted: (new URLSearchParams(search)).get('formSubmitted'),
                courses
              })
            })}
          </div>
        </div>



      </div>

      {isLoading && (!courses || courses.length < 1) ? (
        <div class="bg-opacity-90 bg-white fixed grid h-screen items-center text-center top-0 w-screen" style={{
          zIndex: 99999
        }}>
          <div class="gap-4 grid p-6 text-2xl text-center">
            <svg version="1.1" id="L7" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 100 100" enable-background="new 0 0 100 100" class="block mx-auto w-20">
              <path fill="#3D74BA" d="M31.6,3.5C5.9,13.6-6.6,42.7,3.5,68.4c10.1,25.7,39.2,38.3,64.9,28.1l-3.1-7.9c-21.3,8.4-45.4-2-53.8-23.3 c-8.4-21.3,2-45.4,23.3-53.8L31.6,3.5z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="2s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path>
              <path fill="#ADBD23" d="M42.3,39.6c5.7-4.3,13.9-3.1,18.1,2.7c4.3,5.7,3.1,13.9-2.7,18.1l4.1,5.5c8.8-6.5,10.6-19,4.1-27.7 c-6.5-8.8-19-10.6-27.7-4.1L42.3,39.6z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="1s" from="0 50 50" to="-360 50 50" repeatCount="indefinite"></animateTransform></path>
              <path fill="#F9BB4E" d="M82,35.7C74.1,18,53.4,10.1,35.7,18S10.1,46.6,18,64.3l7.6-3.4c-6-13.5,0-29.3,13.5-35.3s29.3,0,35.3,13.5 L82,35.7z"><animateTransform attributeName="transform" attributeType="XML" type="rotate" dur="2s" from="0 50 50" to="360 50 50" repeatCount="indefinite"></animateTransform></path>
            </svg>

            <span>Loading...</span>
          </div>
        </div>

      ) : null}
    </Fragment>
  );
}

export default CourseWizard;
